import React from 'react';
import { get, isEqual } from 'lodash';
import InputMaskModule from 'react-text-mask';
import { wrapValue, ignoreAutofillProps, shouldAutofocus } from '@helpers/helpers';
import { withTranslation } from 'react-i18next';
import maskMap from './masks';
import { Input } from '../../elements';
import { FormInput } from '../../blocks';
import classNames from 'classnames';


type Props = {
  onChange: Function,
  value?: string | number,
  defaultValue?: string | number,
  placeholder?: string,
  type: string,
  label?: string,
  className?: string,
  hint?: string,
  error?: string,
  name?: string,
  id?: string,
  disabled?: boolean,
  required?: boolean,
  uncontrolled?: boolean,
  i18n: Object,
  autofocus?: boolean,
  mobileAutofocus?: boolean,
  elementRef?: any,
  languageOverride?: boolean
};

const clean = (str: string, chars: Array<string> = ['\\', '.', '-', '/']) =>
  String(str).replace(new RegExp(`[${chars.map(i => `\\${i}`).join()}]`, 'g'), '');


class InputMask extends React.Component<Props> {
  constructor(props: Props) {
    super(props);

    this.maskModule = React.createRef();
  }

  shouldComponentUpdate(nextProps) {
    return !isEqual(this.props, nextProps);
  }

  render() {
    const {
      placeholder,
      value, label, hint,
      error, type, defaultValue,
      i18n, name, id, disabled, required, className,
      uncontrolled, elementRef, languageOverride,
    } = this.props;

    const autofocus = shouldAutofocus(this.props.autofocus, this.props.mobileAutofocus);

    if (type === 'time') {
      return (
        <FormInput
          label={label}
          required={required}
          htmlFor={id}
          error={error}
          hint={hint}
          className={classNames(className, `input-name-${name}`)}
        >
          <input
            type="time"
            name={name}
            id={id}
            value={value || ''}
            placeholder={placeholder || ''}
            disabled={disabled}
            onChange={this.handleNativeTimeChange}
            autoFocus={autofocus}
            {...(elementRef ? { ref: elementRef } : {})}
            {...ignoreAutofillProps}
          />
        </FormInput>
      );
    }

    if (type === 'currency') {
      return (
        <div onClick={e => e.stopPropagation()}>
          <Input
            type="currency"
            placeholder={placeholder}
            name={name}
            label={label}
            {...wrapValue(defaultValue, value, uncontrolled)}
            error={error || ''}
            hint={hint}
            onChange={this.handleOnChangeCurrency}
            disabled={disabled === true}
            required={required}
            {...(elementRef ? { ref: elementRef } : {})}
            id={id}
            autofocus={this.props.autofocus}
            mobileAutofocus={this.props.mobileAutofocus}
          />
        </div>
      );
    }

    let settings;
    if (languageOverride === true) {
      settings = maskMap('other');
    } else {
      settings = maskMap(get(i18n, 'language', '').toLowerCase());
    }

    const pipe = get(settings, [type, 'pipe'], null);
    const filter = get(settings, [type, 'filter'], []);
    const maskConf = get(settings, [type, 'mask']);
    const inputType = get(settings, [type, 'type']);

    return (
      <FormInput
        label={label}
        required={required}
        htmlFor={id}
        onLabelClick={this.handleLabelClick}
        error={error}
        hint={hint}
        className={classNames(className, `input-name-${name}`)}
      >
        {maskConf && (
          <InputMaskModule
            keepCharPositions={false}
            showMask={false}
            guide={false}
            placeholderChar="_"
            type={inputType}
            name={name}
            mask={settings[type].mask}
            id={id}
            {...wrapValue(defaultValue, clean(value || '', filter), uncontrolled)}
            placeholder={placeholder || ''}
            disabled={disabled}
            onChange={this.handleOnChange}
            {...(pipe ? { pipe } : {})}
            {...(elementRef ? { ref: elementRef } : {})}
            autoFocus={autofocus}
            {...ignoreAutofillProps}
          />
        )}
        {!maskConf && (
          <Input
            type="text"
            autofocus={this.props.autofocus}
            mobileAutofocus={this.props.mobileAutofocus}
            {...(elementRef ? { ref: elementRef } : {})}
            value={value || ''}
            name={name}
            id={id}
            placeholder={placeholder || ''}
            onChange={this.handleOnChange}
          />
        )}
      </FormInput>
    );
  }

  maskModule: any;

  handleOnChangeCurrency = (data) => {
    const {
      onChange,
    } = this.props;
    onChange({ name: data.name, value: data.value });
  };

  handleNativeTimeChange = (e) => {
    const { onChange, name } = this.props;
    onChange({ name, value: e.target.value });
  };

  handleOnChange = (e) => {
    const {
      type, onChange, name, i18n, languageOverride,
    } = this.props;

    let settings;
    if (languageOverride) {
      settings = maskMap('other');
    } else {
      settings = maskMap(get(i18n, 'language', '').toLowerCase());
    }

    const value = get(settings, [type, 'mask']) ? e.target.value : get(e, 'value', '');
    const filters = get(settings, [type, 'filter'], []);
    const cleanValue = clean(value || '', filters);

    onChange({ name, value: cleanValue });
  };
}

export default withTranslation()(InputMask);
